import React, { useState, useCallback, useRef } from 'react';
import FiscalYearContext from './FiscalYearContext';
import fiscalYearModel from '@/model/sales-rep/FiscalYear';
import { useLoading } from '@/utils/LoadingUtil';
import {
  getFiscalYears,
  addFiscalYear,
  updateFiscalYear,
} from '@/services/fiscalYearService';
import { showAlert } from '@/components';
import { handleApiError } from '@/lib/handleApiError';

const FiscalYearContextProvider = ({ children }) => {
  const { fiscalYear, setFiscalYear, initialState } = fiscalYearModel();
  const { isLoading, startLoading, stopLoading } = useLoading();
  const [fiscalYearsData, setFiscalYearsData] = useState([]);
  const [error, setError] = useState(null);
  const fiscalYearRef = useRef(fiscalYear);
  const [newFiscalYear, setNewFiscalYear] = useState('');
  const [inputError, setInputError] = useState('');

  const setFiscalYearSafely = useCallback(
    (newFiscalYear) => {
      fiscalYearRef.current = newFiscalYear;
      setFiscalYear(newFiscalYear);
    },
    [setFiscalYear],
  );

  const fetchFiscalYears = useCallback(async () => {
    startLoading();
    try {
      const data = await getFiscalYears();
      setFiscalYearsData(data);
    } catch (error) {
      handleApiError(
        'error',
        error.response?.data || 'An error occurred while fetching fiscal years',
        error?.response?.status,
      );
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading]);

  const createFiscalYear = useCallback(
    async (fiscalYear) => {
      startLoading();
      try {
        const data = await addFiscalYear(fiscalYear);
        await fetchFiscalYears();
        showAlert('success', 'Fiscal year added successfully');
      } catch (error) {
        handleApiError(
          'error',
          error.response?.data || 'An error occurred while adding fiscal year',
          error?.response?.status,
        );
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading, fetchFiscalYears],
  );

  const modifyFiscalYear = useCallback(
    async (fiscalYear) => {
      startLoading();
      try {
        const data = await updateFiscalYear(fiscalYear);
        setFiscalYearsData((prev) =>
          prev.map((fy) => (fy.id === data.id ? data : fy)),
        );
        showAlert('success', 'Fiscal year updated successfully');
        fetchFiscalYears();
      } catch (error) {
        handleApiError(
          'error',
          error.response?.data ||
            'An error occurred while updating fiscal year',
          error?.response?.status,
        );
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading],
  );

  const handleAddNewFiscalYear = () => {
    const currentYear = new Date().getFullYear();
    const [startYear, endYear] = newFiscalYear.split('-').map(Number);

    if (
      startYear < 2000 ||
      endYear > currentYear + 1 ||
      endYear !== startYear + 1
    ) {
      setInputError(
        "Invalid fiscal year format. Please use 'YYYY-YYYY' format.",
      );
      return;
    }

    setInputError('');
    createFiscalYear(newFiscalYear);
  };

  return (
    <FiscalYearContext.Provider
      value={{
        fiscalYear: fiscalYearRef.current,
        setFiscalYear: setFiscalYearSafely,
        initialState,
        fiscalYearsData,
        fetchFiscalYears,
        createFiscalYear,
        modifyFiscalYear,
        error,
        isLoading,
      }}
    >
      {children}
    </FiscalYearContext.Provider>
  );
};

export default FiscalYearContextProvider;
export const useFiscalYearContext = () => React.useContext(FiscalYearContext);
